import { KeyboardDoubleArrowUpRounded } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  createTheme,
  Fade,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import { useCache } from "@shared/frontend/hooks/use-cache.hook";
import { useUntil } from "@shared/frontend/hooks/use-until.hook";
import {
  type ColumnSizingState,
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type VisibilityState,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { pick, throttle } from "lodash";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isMobile } from "react-device-detect";
import { repo, type MembersToInclude } from "remult";
import { useArrayState } from "../hooks/use-array-state.hook";
import { useSessionStorage } from "../hooks/use-storage.hook";
import { COLORS } from "../scss/Variables";
import "./Table.scss";
import {
  COLUMN_RESIZE_MAX_SIZE,
  COLUMN_RESIZE_MIN_SIZE,
  fillColumnSizing,
  getRowId,
  mergeIncludes,
  useColumnFilters,
} from "./functions";
import HyperTableAddRowButton from "./inner-components/HyperTableAddRowButton";
import { HyperTableHeaderCell } from "./inner-components/HyperTableHeaderCell";
import { HyperTableNoResults } from "./inner-components/HyperTableNoResults";
import { HyperTableResultCounter } from "./inner-components/HyperTableResultCounter";
import { HyperTableRow } from "./inner-components/HyperTableRow";
import HyperTablePaginate from "./inner-components/hyper-table-paginate/HyperTablePaginate";
import type {
  Column as ColumnType,
  HyperTableContextValue,
  HyperTableProps,
} from "./types";
import { useHyperTableFind } from "./useHyperTableFind";

const HyperTableContext = createContext<null | HyperTableContextValue>(null);

const theme = createTheme({
  palette: {
    primary: {
      main: COLORS["main-color-light"],
    },
    secondary: {
      main: COLORS["blue-2"],
    },
  },
});

export function useHyperTable<T>() {
  const ctx = useContext(HyperTableContext);
  if (!ctx) throw new Error("Hyper table context called outside of provider");
  return ctx as HyperTableContextValue<T>;
}

const HyperTable = <Entity extends any>(
  props: HyperTableProps<Entity>,
): React.ReactNode => {
  const {
    children,
    entity,
    tableSettings = {},
    findConfig = {},
    ctx,
    onRowClick,
    getRowClassName,
    onLoad,
    onDataFirstLoad,
    components = {},
    tableId,
    title,
    queryOptions = {},
    onPageChange,
    onDisplayedDataChange,
    onFiltersChange,
    renderSubRows,
    onTableLoading
  } = props;

  const hideTableBody = components.noResults === null;

  const { getSubRows } = tableSettings;

  const tableRef = useRef<TanstackTable<Entity> | null>(null);

  const [columns, columnActions] = useArrayState<ColumnType<Entity>>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const storageBaseNameFor = useCache(
    (forWhat: string) => {
      return `hyper_table_${repo(entity).metadata.key}_${tableId}_${forWhat}`;
    },
    [entity, tableId],
  );

  const [columnVisibility, setColumnVisibility] =
    useSessionStorage<VisibilityState>(
      storageBaseNameFor("column-visibility"),
      {},
      (item) => item !== null && typeof item === "object",
    );

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [containerWidth, setContainerWidth] = useState(0);

  const visibleColumnSizes = useMemo(
    () =>
      columns
        .filter((col) => col.id && columnVisibility[col.id] !== false)
        .map((col) => ({
          id: col.id!,
          size: col.size,
          minSize: col.minSize,
          maxSize: col.maxSize,
          enableResizing: col.enableResizing,
        })),
    [columns, columnVisibility],
  );

  const [expanded, setExpanded] = useSessionStorage<ExpandedState>(
    storageBaseNameFor("expanded"),
    {},
  );

  const { columnFilters, columnFilterActions, columnFiltersEnsured } =
    useColumnFilters(storageBaseNameFor("filters"), entity);

  // Passive notify only - do not useLayoutEffect (avoids parent setState during mount).
  useEffect(() => {
    onFiltersChange?.(columnFilters);
  }, [columnFilters, onFiltersChange]);

  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const scrollableTableContainer = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = scrollableTableContainer.current;
    if (!el) return;

    const updateWidth = () => setContainerWidth(el.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hideTableBody]);

  useLayoutEffect(() => {
    if (!containerWidth || visibleColumnSizes.length === 0) return;
    if (tableRef.current?.getState().columnSizingInfo.isResizingColumn) return;
    setColumnSizing((prev) => {
      const next = fillColumnSizing(prev, visibleColumnSizes, containerWidth);
      const nextKeys = Object.keys(next);
      if (
        Object.keys(prev).length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [containerWidth, visibleColumnSizes]);

  const scrollToTop = useCallback(() => {
    if (scrollableTableContainer.current) {
      scrollableTableContainer.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [scrollableTableContainer]);

  const handlePageReset = useCallback(() => {
    scrollToTop();
    if (tableRef.current) {
      tableRef.current.toggleAllPageRowsSelected(false);
    }
  }, [scrollToTop]);

  const enabled =
    columnFiltersEnsured &&
    columns.length === columnActions.__currentLength &&
    columnActions.__currentLength > 0 &&
    (findConfig.enabled ?? true);

  const finalInclude = useMemo<MembersToInclude<Entity>>(
    () =>
      mergeIncludes([
        ...columns.map((v) => v.include),
        findConfig.include ?? {},
      ]),
    [
      JSON.stringify(columns.map((v) => v.include)),
      JSON.stringify(findConfig.include ?? {}),
    ],
  );

  const findResult = useHyperTableFind(
    entity,
    {
      sessionStorageKey: storageBaseNameFor("pagination"),
      queryOptions,
      uniqueQueryId: tableId,
      enabled,
      include: finalInclude,
      initialOrderBy: findConfig.initialOrderBy,
      pinnedOrderBy: findConfig.pinnedOrderBy,
      where: findConfig.where ?? {},
      initialLimit: findConfig.limit,
      onPageReset: handlePageReset,
      mapRows: findConfig.mapRows,
    },
    tableRef.current?.getState().sorting ?? [],
    columnFilters.map((v) => v.value),
  );

  const { pagination, setPagination, tableProps, refetch, isLoading } =
    findResult;

  useEffect(() => {
    // Reset selection when filters change
    tableRef.current?.setRowSelection({});
    setAllSelected(false);
  }, [JSON.stringify(findResult.where)]);

  useEffect(() => {
    if (findConfig.limit) {
      const l = findConfig.limit;
      setPagination((prev) => ({
        ...prev,
        pageSize: l,
      }));
    }
  }, [findConfig.limit, setPagination]);

  useEffect(() => {
    if (!isLoading) {
      onPageChange?.(pagination.pageIndex);
    }
  }, [pagination.pageIndex, isLoading]);

  useEffect(() => {
    onDisplayedDataChange?.(tableProps.data);
  }, [tableProps.data]);

  useEffect(() => {
    onTableLoading?.(isLoading);
  }, [isLoading]);

  const { state: overrideState = {}, ...restOverride } = tableSettings;

  const table = useReactTable<Entity>({
    columns: columns.length === columnActions.__currentLength ? columns : [], // Make sure that the columns are loaded at the same time
    state: {
      columnFilters: [],
      pagination,
      expanded,
      columnVisibility,
      ...overrideState,
      columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: () => {
      throw new Error(
        "dont use like this, use `filterActions` from hyper table context",
      );
    },
    // EnableSortingRemoval: false,
    getRowId: (row) => getRowId(entity, row),
    defaultColumn: {
      minSize: COLUMN_RESIZE_MIN_SIZE,
      maxSize: COLUMN_RESIZE_MAX_SIZE,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    ...tableProps,
    ...restOverride,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    columnResizeDirection: "rtl",
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const lockedId =
          tableRef.current?.getState().columnSizingInfo.isResizingColumn;
        if (!containerWidth) return next;
        return fillColumnSizing(
          next,
          visibleColumnSizes,
          containerWidth,
          typeof lockedId === "string" ? lockedId : undefined,
        );
      });
    },
    getSubRows,
  });
  tableRef.current = table;

  const getEntity = useCallback(() => entity, [entity]);

  const handleTableContainerScroll = useCallback(
    throttle(() => {
      if (scrollableTableContainer.current) {
        setShowScrollButton(scrollableTableContainer.current.scrollTop > 250);
      }
    }, 350),
    [setShowScrollButton],
  );

  const selectedIds = useMemo(() => {
    return Object.entries(table.getState().rowSelection)
      .filter((v) => v[1])
      .map((v) => v[0])
      .sort();
  }, [JSON.stringify(table.getState().rowSelection)]);

  const contextValue: HyperTableContextValue<Entity> = {
    columns,
    filters: columnFilters,
    filterActions: columnFilterActions,
    columnActions,
    getEntity,
    table,
    refetch,
    tableId,
    storageBaseNameFor,
    data: tableProps.data,
    allSelected,
    setAllSelected,
    findResult,
    finalWhere: findResult.where,
    selectedIds,
    finalInclude,
    tableSettings,
    ...pick(findResult, "pagination", "setPagination"),
  };

  if (ctx) (ctx.current as any) = contextValue;

  useUntil(() => {
    if (enabled) {
      columns.forEach((col) => {
        if (col.defaultPinned) {
          const c = table.getColumn(col.id);
          c?.pin("left");
        }
      });
      onLoad?.(contextValue);
    }
    return enabled;
  });

  useUntil(() => {
    const loadedData = tableProps.data;
    if (loadedData.length) {
      onDataFirstLoad?.(contextValue);
    }
    return loadedData.length !== 0;
  });

  const C =
    HyperTableContext as React.Context<HyperTableContextValue<Entity> | null>;
  return (
    <C.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <Stack width="100%" height="100%" minHeight={0} overflow="hidden">
          {title && ["string", "number"].includes(typeof title) ? (
            <span>{title}</span>
          ) : (
            <Stack
              width="100%"
              height={title ? "2.3rem" : "0"}
              flexShrink={0}
              direction="row"
              justifyContent="space-between"
            >
              {title}
              {components.addRowButton &&
                (typeof components.addRowButton === "function" ? (
                  <components.addRowButton />
                ) : (
                  <HyperTableAddRowButton {...components.addRowButton} />
                ))}
            </Stack>
          )}
          <Stack
            width="100%"
            direction="column"
            flex={hideTableBody ? 1 : "0 0 auto"}
            minHeight={hideTableBody ? 0 : undefined}
            overflow={hideTableBody ? "hidden" : undefined}
          >
            {children}
          </Stack>
          {!hideTableBody && (
          <TableContainer
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
            ref={scrollableTableContainer}
            onScroll={handleTableContainerScroll}
            className="data-table-sub-container"
          >
            <Table
              sx={{
                width: "100%",
                minWidth:
                  containerWidth &&
                  table.getTotalSize() > containerWidth + 1
                    ? table.getTotalSize()
                    : undefined,
                tableLayout: "fixed",
              }}
              className="table"
            >
              <colgroup>
                {table.getFlatHeaders().map((header) => (
                  <col key={header.id} style={{ width: header.getSize() }} />
                ))}
              </colgroup>
              <TableHead className="table-head">
                <TableRow className="table-row">
                  {table.getFlatHeaders()?.map((header, index, allHeaders) => (
                    <HyperTableHeaderCell
                      key={header.id}
                      header={header}
                      index={index}
                      allHeaders={allHeaders}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className="table-body">
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows?.map((row) => {
                    return (
                      <React.Fragment key={row.id}>
                        {renderSubRows && row.getParentRow()?.original ? (
                          row.index === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={table.getFlatHeaders().length}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  width="100%"
                                  height="100%"
                                >
                                  {new Array(row.depth).fill("").map((_, i) => (
                                    <Stack
                                      key={"placeholder" + i}
                                      className="placeholder"
                                      width="3rem"
                                    />
                                  ))}
                                  {renderSubRows(row.getParentRow()?.original)}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ) : null
                        ) : (
                          <HyperTableRow
                            row={row}
                            className={getRowClassName?.(row)}
                            highlighted={
                              getRowClassName?.(row) === "table-row--called"
                            }
                            onClick={
                              onRowClick ? (e) => onRowClick(e, row) : undefined
                            }
                          />
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  components.noResults ? (
                    <components.noResults />
                  ) : (
                    <>
                      {components.noResults === null ? null : <HyperTableNoResults />}
                    </>
                  )
                )}
              </TableBody>
            </Table>
            <Stack
              marginTop={components.loading !== null ? 2 : 0}
              marginBottom={components.loading !== null ? 2 : 0}
              width="100%"
              height={components.loading !== null ? "2rem" : 0}
              alignItems={isMobile ? "start" : "center"}
            >
              {isLoading &&
                (typeof components.loading === "function" ? (
                  <components.loading ctx={contextValue} />
                ) : (
                  (components.loading ?? (
                    <CircularProgress disableShrink size={30} />
                  ))
                ))}
            </Stack>
          </TableContainer>
          )}
          {!hideTableBody && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 35,
              flexShrink: 0,
            }}
          >
            {components.rowCounter !== null && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 16,
                  transform: "translateY(-50%)",
                  width: "100%",
                }}
              >
                <HyperTableResultCounter
                  overrideComponent={components.rowCounter}
                />
              </Box>
            )}

            {components.paginate !== null && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 30,
                  transform: "translateY(-50%)",
                }}
              >
                <HyperTablePaginate overrideComponent={components.paginate} />
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: 0,
              }}
            >
              {components.scrollToTop ? (
                <components.scrollToTop onClick={scrollToTop} />
              ) : (
                <Tooltip title="חזרה למעלה" placement="left">
                  <Fade timeout={100} in={showScrollButton}>
                    <IconButton
                      sx={{ left: 16 }}
                      color="default"
                      onClick={scrollToTop}
                    >
                      <KeyboardDoubleArrowUpRounded />
                    </IconButton>
                  </Fade>
                </Tooltip>
              )}
            </Box>
          </Box>
          )}
        </Stack>
      </ThemeProvider>
    </C.Provider>
  );
};

export { HyperTable };
